import React, { FunctionComponent, useMemo } from 'react';
import styles from './styles.module.scss';
import Image from 'next/image';
import classNames from 'classnames';
import { SupportedCurrencies } from '@cademy-package/sdk-component-types';

type VoucherPreviewProps = {
    amount: number;
    message?: string;
    recipientName?: string;
    buyerName?: string;
    printout: boolean;
    code?: string;
    logo_url?: string;
    cover_url?: string;
    educator_name: string;
    website?: string;
    currency: SupportedCurrencies;
};

export const GiftPreview: FunctionComponent<VoucherPreviewProps> = ({
    amount,
    message,
    recipientName,
    buyerName,
    printout = false,
    code,
    logo_url,
    cover_url,
    educator_name,
    website,
    currency,
}) => {
    const date = new Date();

    const logoUrl = logo_url || `${process.env.NEXT_PUBLIC_ASSETS_URL}/logos/logo-resized.svg`;
    const coverUrl = cover_url || `${process.env.NEXT_PUBLIC_ASSETS_URL}/images/gift-banner.png`;

    const currencySymbol = useMemo(() => {
        try {
            const format = new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: currency,
                currencyDisplay: 'narrowSymbol',
            })
                .formatToParts(0)
                .find((part) => part.type === 'currency');

            if (format) {
                return format.value;
            }

            return '';
        } catch (error) {
            console.error(error);
            return '';
        }
    }, [currency]);

    return (
        <div className={classNames({ [`${styles.container}`]: !printout })}>
            <Image
                className={styles.banner_img}
                alt="Gift card decorative preview"
                src={coverUrl}
                width={500}
                height={200}
            />
            <div className={styles.inner_container}>
                <div className={styles.title_and_logo}>
                    <h3 className={styles.heading}>A special gift for you</h3>
                    <div className={`${styles.logo} rounded_image`}>
                        <Image src={logoUrl} alt="Educator logo" fill />
                    </div>
                </div>
                <p>Hey {recipientName || ' there'},</p>

                <p>
                    {buyerName || 'Someone '} just sent you a gift card for{' '}
                    <b>
                        {currencySymbol}
                        {amount || '20'}
                    </b>{' '}
                    (<em>lucky you</em>). Use it to book any class or workshop from{' '}
                    {educator_name ||
                        "Cademy - the UK's largest marketplace for local classes, courses & workshops"}
                    .
                </p>
                {message && (
                    <p>
                        <em>{message}</em>
                    </p>
                )}
                <div className={styles.redemption_code_container}>
                    <span className={styles.redemption_code_heading}>
                        Your voucher redemption code:
                    </span>
                    <span className={styles.redemption_code}>{code || 'XXXX XXXX XXXX XXXX'}</span>
                </div>

                <p className="font-size-sm">
                    Apply your voucher code on the checkout page of any course from {educator_name}{' '}
                    before
                    {` ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear() + 1}.`}
                </p>
            </div>
        </div>
    );
};

export default GiftPreview;
